import { flowRight, pick } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router';
import sizeMe from 'react-sizeme';
import styled, { withTheme } from 'styled-components';

import { NO_AVAILABILITY } from '@zocdoc/components/src/AppointmentPicker/constants/TimesGridStateStoreEvents';
import { white } from '@zocdoc/branding/colors';
import { ContentPrimitives, ControlPrimitives, ControlsAndContent } from '@zocdoc/components/src/AppointmentPicker';
import { breakpoint } from '@zocdoc/components/src/grid';
import { LOGIN_STATE } from '@zocdoc/components/src/Layout';

import addConstraintModals from '../../../../Shared/Modals/addConstraintModals';
import { MARKETING_QUERY_PARAMS, TIMESGRID_QUERY_PARAMS } from '../../../../constants';
import isZocdocBrand from '../../../../utils/isZocdocBrand';
import { BookingSubSectionWithNoLabelOnMobile } from '../BookingSection/BookingSubSection';
import { LOCALIZED_STRINGS } from '../BookingSection/constants';
import addInsuranceToUrls from './addInsuranceToUrls';
import { ProviderAvailabilityTooltip } from '../ProviderAvailability';
import { providerAvailabilityTooltipCookieMatchesBsid } from '../ProviderAvailability/utils';

export class BookingSectionTimesGridWrapperView extends React.PureComponent {
    static propTypes = {
        // used only by screenshot tests
        __renderProviderAvailabilityTooltip: PropTypes.bool,
        __skipTimesGridLoadingState: PropTypes.bool,

        ab: PropTypes.object,
        additionalBookingUrlParams: PropTypes.object,
        isExistingPatient: PropTypes.bool,
        isMobile: PropTypes.bool,

        /**
         * from react-router withRouter() HOC
         */
        location: PropTypes.shape({
            query: PropTypes.object,
        }),

        locationId: PropTypes.string,
        procedureId: PropTypes.string,
        provider: PropTypes.object,
        providerId: PropTypes.string,
        size: PropTypes.object,
        trackTimesgridDataLoaded: PropTypes.func.isRequired,
        trackProviderAvailabilityClickedSearch: PropTypes.func.isRequired,
        trackProviderAvailabilityTooltipRendered: PropTypes.func.isRequired,
        user: PropTypes.shape({
            displayName: PropTypes.string,
            loginState: PropTypes.number,
        }),
    };

    static defaultProps = {
        __skipTimesGridLoadingState: false,
        isMobile: false,
        size: {},
    };

    providerAvailabilityTooltipWasAlreadyShown = false;

    constructor(props) {
        super(props);

        const {
            location,
            theme,
        } = props;

        const additionalBookingUrlParams = {
            directoryId: theme.directoryId,
        };

        if (location) {
            Object.assign(additionalBookingUrlParams, pick(location.query, MARKETING_QUERY_PARAMS));
            Object.assign(additionalBookingUrlParams, pick(location.query, TIMESGRID_QUERY_PARAMS));
        }

        this.providerAvailabilityTooltipWasAlreadyShown = providerAvailabilityTooltipCookieMatchesBsid();

        this.state = {
            additionalBookingUrlParams,
            mounted: false,
            providerAvailabilityData: {},
        };
    }

    componentDidMount() {
        this.setState({
            mounted: true,
        });
    }

    render() {
        const {
            __skipTimesGridLoadingState,
            locationId,
            providerId,
            theme: { directoryId },
        } = this.props;

        if (!providerId || !locationId) {
            return (
                <div data-test="booking-section-timesgrid-empty"></div>
            );
        }

        if (isZocdocBrand(directoryId)) {
            return (
                <Wrapper
                    data-test="booking-section-timesgrid-wrapper"
                >
                    {this.maybeRenderProviderLowAvailabilityTooltip()}
                    <TimesgridWrapper
                        ready={this.state.mounted || __skipTimesGridLoadingState}
                    >
                        {this.renderTimesGrid()}
                    </TimesgridWrapper>
                </Wrapper>
            );
        } else {
            return (
                <BookingSubSectionWithNoLabelOnMobile
                    label={LOCALIZED_STRINGS.TimesGridLabel}
                >
                    <TimesGridStyleWrapper>
                        {this.renderTimesGrid()}
                    </TimesGridStyleWrapper>
                </BookingSubSectionWithNoLabelOnMobile>
            );
        }
    }

    renderTimesGrid() {
        const {
            __skipTimesGridLoadingState,
            insuranceCarrierId,
            insurancePlanId,
            isExistingPatient,
            isMobile,
            locationId,
            pageId,
            procedureId,
            provider,
            providerId,
            referrerType,
            size,
            theme,
            widget,
        } = this.props;

        const isConfirmedMobile = this.forceMobileWhenComponentWidthIsTooSmall(size, isMobile);

        /**
         * there seems to be a bug in AP's handling of doctorLocationIds as an array,
         * possibly in a recent version of component SDK
         */
        return (
            <Timesgrid
                additionalBookingUrlParams={this.state.additionalBookingUrlParams}
                data-test="booking-section-timesgrid"
                directoryId={theme.directoryId}
                doctorLocationIds={[`${providerId}_${locationId}`]}
                handleClickEvent={false}
                insuranceCarrierId={insuranceCarrierId}
                insurancePlanId={insurancePlanId}
                isExistingPatient={isExistingPatient}
                isMobile={isConfirmedMobile}
                loading={!this.state.mounted || !__skipTimesGridLoadingState}
                onDataLoaded={this.handleOnDataLoaded}
                onlyCheckResourceConstraint
                pageId={pageId}
                procedureId={procedureId || '-1'}
                provider={provider}
                referrerType={referrerType}
                searchProcedureId={procedureId}
                shouldJumpToFirstAppointment={isConfirmedMobile}
                updateParameters={() => {}}
                widget={widget}
            />
        );
    }

    maybeRenderProviderLowAvailabilityTooltip() {
        const {
            __renderProviderAvailabilityTooltip,
            provider,
            trackProviderAvailabilityClickedClose,
            trackProviderAvailabilityClickedSearch,
            trackProviderAvailabilityTooltipRendered,
            user,
        } = this.props;

        const {
            providerAvailabilityData,
        } = this.state;

        const {
            relevantSpecialty,
        } = provider;

        let tooltipProps = {
            providerAvailabilityData,
            specialty: relevantSpecialty,
            trackProviderAvailabilityClickedClose,
            trackProviderAvailabilityClickedSearch,
            trackProviderAvailabilityTooltipRendered,
            user,
        };

        // Tooltip properties can be force (e.g., for screenshot testing)
        if (__renderProviderAvailabilityTooltip) {
            tooltipProps = {
                ...tooltipProps,
                providerAvailabilityData: {
                    availability: NO_AVAILABILITY,
                },
                user: {
                    displayName: 'Hey',
                    loginState: LOGIN_STATE.LoggedIn,
                },
            };
        } else if (
            this.providerAvailabilityTooltipWasAlreadyShown
            || this.isReferrerZocdoc()
            || !this.providerHasLowAvailabilityForLoggedInUser()
        ) {
            return null;
        }

        return (
            <LowAvailabilityTooltip
                {...tooltipProps}
                data-test="low-availability-tooltip"
            />
        );
    }

    isReferrerZocdoc() {
        return (
            typeof window !== 'undefined'
            && window.document.referrer.indexOf('zocdoc.com') !== -1
        );
    }

    providerHasLowAvailabilityForLoggedInUser() {
        const {
            user,
        } = this.props;

        const {
            providerAvailabilityData,
        } = this.state;

        if (!user || !providerAvailabilityData) {
            return false;
        }

        const { availability } = providerAvailabilityData;
        const { displayName, loginState } = user;

        return (
            availability === NO_AVAILABILITY && loginState !== LOGIN_STATE.LoggedOut && displayName
        );
    }

    forceMobileWhenComponentWidthIsTooSmall(componentWidth, isMobile) {
        if (componentWidth < 340) {
            return true;
        } else {
            return isMobile;
        }
    }

    handleOnDataLoaded = data => {
        const {
            provider: {
                locations,
            },
            trackTimesgridDataLoaded,
        } = this.props;
        trackTimesgridDataLoaded(data);

        if (locations.length === 1) {
            this.setState({ providerAvailabilityData: data });
        }
    }
}

const Timesgrid = addConstraintModals(addInsuranceToUrls(ControlsAndContent));

const TimesGridStyleWrapper = styled.div`
    background-color: ${white};
    padding-bottom: 12px;

    ${ContentPrimitives.MobileWrapper} {
        ${breakpoint('medium', { down: true })} {
            padding-left: 12px;
        }
    }

    ${ContentPrimitives.MobileNoTimesView} {
        padding-right: 12px;
    }

    ${breakpoint.medium} {
        padding-top: 12px;
        padding-bottom: 24px;
    }
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: row;
`;

const TimesgridWrapper = styled.div`
    background-color: ${white};
    padding-bottom: 12px;
    width: 100%;

    ${ContentPrimitives.DesktopWrapper},
    ${ContentPrimitives.MobileWrapper},
    ${ControlPrimitives.DesktopWrapper},
    ${ControlPrimitives.MobileWrapper} {
        opacity: ${p => p.ready ? 1 : 0};
        transition: opacity 300ms ease-in-out;
    }

    ${ContentPrimitives.MobileWrapper} {
        ${breakpoint('medium', { down: true })} {
            padding-left: 12px;
        }
    }

    ${ContentPrimitives.MobileNoTimesView} {
        padding-right: 12px;
    }

    ${breakpoint.medium} {
        padding-top: 12px;
        padding-bottom: 24px;
    }
`;

const LowAvailabilityTooltip = styled(ProviderAvailabilityTooltip)`
    margin: auto;
`;

export default flowRight(
    withTheme,
    withRouter,
    sizeMe(),
)(BookingSectionTimesGridWrapperView);
